import { useEffect, useState } from 'react'
import MultiImagesUpload from '~/components/common/MultiImagesUpload'
import { AgentRequest } from '~/protos/agents'
import { getFileBase64 } from '~/utils/shared'
import { BaseTaskForm, TaskParams } from '../../BaseTaskForm'

interface GenerateAddFlowFormProps {
  onChange: (task: TaskParams) => void
  withExamplePrefilled?: boolean
  initialTaskData?: TaskParams | null
}

export function IntegrationGeneratorForm({
  onChange,
  withExamplePrefilled,
  initialTaskData
}: GenerateAddFlowFormProps) {
  // Initialize from initialTaskData if editing
  const getInitialDescription = () => {
    if (initialTaskData?.request?.command?.commandString) {
      // Extract description (everything before the image count line)
      const commandString = initialTaskData.request.command.commandString
      const lines = commandString.split('\n')
      // Filter out lines that contain "diagrams uploaded"
      const descriptionLines = lines.filter(line => !line.includes('diagrams uploaded'))
      return descriptionLines.join('\n').trim()
    }
    if (withExamplePrefilled) {
      return `
## Overview
The flow fetches product information from a mysql database and order information from Salesforce.

## Trigger
The flow is invoked when an HTTP GET request is made to the /productsOrders endpoint.
The flow does not require any input payload from the HTTP request.

## Connector Configurations

MySql Database Config:
  host: "10.121.1.10"
  port: "3306"
  user: "user"
  password: "password"
  databaseName: "DEV01"

## Detailed Steps
1. The flow is invoked when an HTTP GET request is made to the /productsOrders endpoint.
2. Fetch data from both the database and Salesforce in parallel.
3. Route 1: Fetch product information from the database from products table.
4. Transform the product response.
5. Route 2: Fetch order information Id, OrderNumber, TotalAmount from order table from Salesforce.
6. Transform the order response.
7. Combine the transformed responses from both routes by gathering and structure the final JSON response.
`.trim()
    }
    return ''
  }

  const [description, setDescription] = useState(getInitialDescription())
  const [flowDescription, setFlowDescription] = useState('')
  const [flowDiagrams, setImages] = useState<File[]>([])

  // Note: Images from initialTaskData are stored as base64 in command.images
  // For editing, we show the count but don't reconstruct File objects
  useEffect(() => {
    if (initialTaskData?.request?.command?.images?.length) {
      const imageCount = initialTaskData.request.command.images.length
      setFlowDescription(`${imageCount} diagrams uploaded (from previous task)`)
    }
  }, [initialTaskData])

  const initialDescription = getInitialDescription()

  useEffect(() => {
    const commandString = `${description}\n${flowDescription}`
    const request = AgentRequest.fromPartial({
      command: { commandString }
    })

    if (flowDiagrams.length > 0) {
      Promise.all(flowDiagrams.map(getFileBase64)).then(base64Diagrams => {
        request.command!.images = flowDiagrams.map((file, index) => ({
          imageBase64Content: base64Diagrams[index],
          imageExtension: file.name.split('.').pop()?.toLowerCase() || 'png'
        }))
      })
    }
    onChange?.({ request })
  }, [description, flowDescription, flowDiagrams, onChange])

  return (
    <>
      <div className='mt-4'>
        <BaseTaskForm
          onChange={(task: TaskParams) => {
            const text = task.request?.command?.commandString
            if (typeof text === 'string') {
              setDescription(text)
            }
          }}
          defaultValue={initialDescription}
          minRows={3}
          placeholder='Describe the flow that you want to add.'
          tooltipTargetClassName='add-your-specs'
          disabled={withExamplePrefilled}
        />
        <>
          <div className='label font-semibold label-lg-bold text-default-700 my-[10px] select-none'>
            Diagrams:
          </div>
          <div className='description text-sm text-default-500 my-[10px] select-none'>
            Upload flow diagrams you want to add.
          </div>
          <MultiImagesUpload
            tooltipTargetClassName='tool-tip-target add-diagrams'
            onChange={images => {
              if (images.length > 0) {
                setFlowDescription(`${images.length} diagrams uploaded`)
              } else {
                setFlowDescription('')
              }
              setImages(images)
            }}
            disabled={withExamplePrefilled}
          />
        </>
      </div>
    </>
  )
}
